// Status pipeline header (P2) — the project's gated workflow rendered as a
// ButtonBase pill row (not MUI Stepper). Click a step → gate checklist popover
// → move (or override with a reason). Blocked is a ghost text toggle; the
// danger StatusPill lives in the page frame row C (`Blocked · needs a reason`
// when no reason is set).
import { useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Popover,
  TextField,
  Tooltip,
} from "@mui/material";
import { useSnackbar } from "notistack";
import confetti from "canvas-confetti";
import { brainTokens } from "../../design/brainTheme";
import { Icon } from "../../mui";
import {
  getTransitionChecks,
  transitionProject,
  setProjectBlocked,
  PROJECT_STATUS_LABELS,
  type GateCheck,
  type Project,
  type ProjectStatus,
} from "../../lib/brain-api";

const c = brainTokens.color;

/**
 * Archived is reachable from the record menu, not the strip. `draft` is no
 * longer a destination (Isaac 2026-08-13) — but projects already sitting in it
 * still have to be able to see where they are, so it is prepended only while it
 * is the current status. `openStep` already refuses the current step, so it
 * remains a place you can leave and never a place you can move to.
 */
const BASE_STRIP: ProjectStatus[] = ["planning", "in_progress", "on_hold", "with_client", "done"];

export function ProjectStatusPipeline({
  project,
  canEdit,
  onChanged,
}: {
  project: Project;
  canEdit: boolean;
  onChanged: (p: Project) => void;
}) {
  const { enqueueSnackbar } = useSnackbar();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [target, setTarget] = useState<ProjectStatus | null>(null);
  const [checks, setChecks] = useState<GateCheck[] | null>(null);
  const [allowed, setAllowed] = useState(false);
  const [override, setOverride] = useState(false);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);

  const strip: ProjectStatus[] =
    project.status === "draft" ? ["draft", ...BASE_STRIP] : BASE_STRIP;
  const currentIdx = strip.indexOf(project.status);

  useEffect(() => {
    if (!target) return;
    setChecks(null);
    getTransitionChecks(project.projectId, target)
      .then((r) => {
        setChecks(r.checks);
        setAllowed(r.allowed);
      })
      .catch((e) => {
        enqueueSnackbar(e instanceof Error ? e.message : "Failed to load gates", { variant: "error" });
        setChecks([]);
        setAllowed(false);
      });
  }, [project.projectId, target, enqueueSnackbar]);

  const openStep = (e: React.MouseEvent<HTMLElement>, s: ProjectStatus) => {
    if (!canEdit || s === project.status) return;
    setTarget(s);
    setOverride(false);
    setReason("");
    setAnchor(e.currentTarget);
  };

  const close = () => {
    setAnchor(null);
    setTarget(null);
  };

  const move = async () => {
    if (!target) return;
    setBusy(true);
    try {
      const r = await transitionProject(project.projectId, target, {
        override: override || undefined,
        reason: reason.trim() || undefined,
      });
      onChanged(r.project);
      enqueueSnackbar(`Moved to ${PROJECT_STATUS_LABELS[target]}`, { variant: "success" });
      if (target === "done") {
        // Isolated from the transition's own error handling — a decorative
        // failure here must never read as "the move failed". zIndex pushed
        // above MUI's Popover/Snackbar (1300/1400) so the burst isn't hidden
        // behind the closing popover or the success toast.
        try {
          void confetti({ particleCount: 120, spread: 75, origin: { y: 0.6 }, zIndex: 9999 });
        } catch (ce) {
          console.error("[confetti]", ce instanceof Error ? ce.message : String(ce));
        }
      }
      close();
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Transition failed", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  /** Toggle blocked on/off with no reason dialog — empty reason → row C pill. */
  const toggleBlocked = async () => {
    if (!canEdit || busy) return;
    setBusy(true);
    try {
      const p = await setProjectBlocked(project.projectId, !project.blocked);
      onChanged(p);
    } catch (e) {
      enqueueSnackbar(e instanceof Error ? e.message : "Failed to update blocked flag", { variant: "error" });
    } finally {
      setBusy(false);
    }
  };

  const failedRequired = (checks ?? []).filter((x) => !x.ok && !x.advisory);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: "8px" }}>
      <Box sx={{ display: "flex", alignItems: "center" }} aria-label="Project status pipeline">
        {strip.map((s, i) => {
          const isCurrent = s === project.status;
          const isPast = currentIdx >= 0 && i < currentIdx;
          // Status moves freely in either direction (server: "backward moves are
          // always free", no gates) — every non-current step stays clickable.
          const clickable = canEdit && !isCurrent;
          return (
            <Box key={s} sx={{ display: "flex", alignItems: "center" }}>
              {i > 0 && (
                <Box sx={{ height: "1px", width: "18px", background: c.tealTintBorder, mx: "6px" }} />
              )}
              {isPast ? (
                <Tooltip title={clickable ? `Move back to ${PROJECT_STATUS_LABELS[s]}` : ""}>
                  <Box
                    component={ButtonBase}
                    onClick={(e: React.MouseEvent<HTMLElement>) => openStep(e, s)}
                    disabled={!clickable}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "4px",
                      color: c.tealInk,
                      fontSize: 12,
                      fontWeight: 500,
                      fontFamily: "inherit",
                      borderRadius: `${brainTokens.radius.pill}px`,
                      padding: "4px 8px",
                      cursor: clickable ? "pointer" : "default",
                      "&:hover": clickable ? { background: c.tealTint } : undefined,
                      "&.Mui-disabled": { opacity: 1 },
                    }}
                  >
                    <Icon name="check" size={12} stroke={2} />
                    <Box component="span">{PROJECT_STATUS_LABELS[s]}</Box>
                  </Box>
                </Tooltip>
              ) : (
                <Tooltip title={isCurrent ? "Current status" : clickable ? `Move to ${PROJECT_STATUS_LABELS[s]}` : ""}>
                  {/* span wrapper: the current step renders DISABLED, and a
                      disabled element fires no events for Tooltip to hang off. */}
                  <Box component="span" sx={{ display: "inline-flex" }}>
                  <Box
                    component={ButtonBase}
                    onClick={(e: React.MouseEvent<HTMLElement>) => openStep(e, s)}
                    disabled={!clickable}
                    sx={{
                      border: `1px solid ${isCurrent ? c.teal : c.border}`,
                      background: isCurrent ? c.teal : c.white,
                      color: isCurrent ? c.white : c.faint,
                      borderRadius: `${brainTokens.radius.pill}px`,
                      padding: isCurrent ? "5px 13px" : "4px 12px",
                      fontSize: 12,
                      lineHeight: 1,
                      fontWeight: isCurrent ? 600 : 400,
                      fontFamily: "inherit",
                      cursor: clickable ? "pointer" : "default",
                      display: "inline-flex",
                      alignItems: "center",
                      "&.Mui-disabled": { opacity: 1 },
                    }}
                  >
                    {PROJECT_STATUS_LABELS[s]}
                  </Box>
                  </Box>
                </Tooltip>
              )}
            </Box>
          );
        })}
        {project.status === "archived" && (
          <Box sx={{ ml: 1, fontSize: 12.5, color: c.muted2 }}>Archived</Box>
        )}
      </Box>

      {canEdit && (
        <Box
          component={ButtonBase}
          onClick={() => void toggleBlocked()}
          disabled={busy}
          sx={{
            fontSize: 12,
            color: project.blocked ? c.dangerText : c.muted2,
            fontFamily: "inherit",
            cursor: "pointer",
          }}
        >
          {project.blocked ? "Clear blocked" : "Mark blocked"}
        </Box>
      )}

      <Popover
        open={Boolean(anchor && target)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box sx={{ p: 2, width: 340 }}>
          <Box sx={{ fontWeight: 700, fontSize: 13.5, mb: 1 }}>
            Move to {target ? PROJECT_STATUS_LABELS[target] : ""}
          </Box>
          {checks === null ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <CircularProgress size={20} />
            </Box>
          ) : checks.length === 0 ? (
            <Box sx={{ fontSize: 12.5, color: c.muted2, mb: 1 }}>No gates for this move.</Box>
          ) : (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75, mb: 1.25 }}>
              {checks.map((chk) => (
                <Box key={chk.key} sx={{ display: "flex", gap: 0.75, alignItems: "baseline", fontSize: 12.5 }}>
                  <Box component="span" sx={{ color: chk.ok ? c.successText : chk.advisory ? c.warnText2 : c.dangerText }}>
                    {chk.ok ? "✓" : chk.advisory ? "•" : "✕"}
                  </Box>
                  <Box component="span" sx={{ color: chk.ok ? c.ink : c.muted2 }}>
                    {chk.label}
                    {chk.detail && <Box component="span" sx={{ color: c.muted2 }}> — {chk.detail}</Box>}
                    {chk.advisory && !chk.ok && <Box component="span" sx={{ color: c.warnText2 }}> (advisory)</Box>}
                  </Box>
                </Box>
              ))}
            </Box>
          )}
          {checks !== null && failedRequired.length > 0 && (
            <>
              <FormControlLabel
                control={<Checkbox size="small" checked={override} onChange={(e) => setOverride(e.target.checked)} />}
                label={<Box sx={{ fontSize: 12.5 }}>Override gates (owner/deputy, reason required)</Box>}
              />
              {override && (
                <TextField
                  size="small"
                  fullWidth
                  label="Override reason"
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                  sx={{ mt: 0.75 }}
                />
              )}
            </>
          )}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 1.5 }}>
            <Button size="small" onClick={close}>Cancel</Button>
            <Button
              size="small"
              variant="contained"
              disabled={busy || checks === null || (!allowed && (!override || !reason.trim()))}
              onClick={() => void move()}
            >
              Move
            </Button>
          </Box>
        </Box>
      </Popover>
    </Box>
  );
}
